import { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { imageUrl, listBusinessProfiles, updateBusinessProfile, uploadImages } from '../api';

// homepage_refactor.md - the website admin configures each business's "About this business"
// content shown in the homepage's Businesses popup: registration details, years of experience,
// and management team profiles (with photos). One doc per vertical, but editable from ANY
// vertical's AdminDashboard (same admin login, same platform-wide reach as Business analytics /
// Platform settings) - hence the vertical picker below.
const VERTICAL_LABEL = {
  jewelry: 'Jewellery Rental',
  car_rental: 'Car Rental',
  real_estate: 'Real Estate',
  hospitality: 'Hospitality',
  gym: 'Gym Memberships'
};
const VERTICAL_ORDER = ['jewelry', 'car_rental', 'real_estate', 'hospitality', 'gym'];

const emptyMember = { name: '', designation: '', photoUrl: '', bio: '' };

function toFormState(profile) {
  return {
    registeredName: profile.registeredName || '',
    registrationNumber: profile.registrationNumber || '',
    registeredSince: profile.registeredSince ? String(profile.registeredSince).slice(0, 10) : '',
    yearsExperience: profile.yearsExperience ?? '',
    about: profile.about || '',
    management: profile.management?.length ? profile.management.map((m) => ({ ...m })) : []
  };
}

export default function BusinessProfilePanel() {
  const [vertical, setVertical] = useState('jewelry');
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setMsg('');
    setError('');
    listBusinessProfiles()
      .then((profiles) => {
        if (cancelled) return;
        const profile = profiles.find((p) => p.vertical === vertical) || { vertical };
        setForm(toFormState(profile));
      })
      .catch(() => { if (!cancelled) setError('Could not load business profiles.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [vertical]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const setMember = (idx, field) => (e) => setForm((f) => {
    const management = f.management.map((m, i) => (i === idx ? { ...m, [field]: e.target.value } : m));
    return { ...f, management };
  });

  const addMember = () => setForm((f) => ({ ...f, management: [...f.management, { ...emptyMember }] }));
  const removeMember = (idx) => setForm((f) => ({ ...f, management: f.management.filter((_, i) => i !== idx) }));

  const uploadMemberPhoto = async (idx, file) => {
    if (!file) return;
    setUploadingIdx(idx);
    setError('');
    try {
      const [url] = await uploadImages([file]);
      setForm((f) => ({
        ...f,
        management: f.management.map((m, i) => (i === idx ? { ...m, photoUrl: url } : m))
      }));
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to upload that photo.');
    } finally {
      setUploadingIdx(null);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setMsg('');
    setError('');
    setSaving(true);
    try {
      const payload = {
        registeredName: form.registeredName,
        registrationNumber: form.registrationNumber,
        registeredSince: form.registeredSince || null,
        yearsExperience: form.yearsExperience === '' ? null : Number(form.yearsExperience),
        about: form.about,
        management: form.management.filter((m) => m.name.trim() || m.designation.trim() || m.bio.trim() || m.photoUrl)
      };
      const saved = await updateBusinessProfile(vertical, payload);
      setForm(toFormState(saved));
      setMsg('Saved. The homepage Businesses popup now shows this.');
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to save this business profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <p className="text-muted small">
        Shown in the homepage's "Businesses" popup, under each business's "About this business" panel.
      </p>
      <Form.Group className="mb-3" style={{ maxWidth: 280 }}>
        <Form.Label className="small mb-0">Business</Form.Label>
        <Form.Select size="sm" value={vertical} onChange={(e) => setVertical(e.target.value)}>
          {VERTICAL_ORDER.map((v) => (
            <option key={v} value={v}>{VERTICAL_LABEL[v]}</option>
          ))}
        </Form.Select>
      </Form.Group>

      {msg && <Alert variant="success" className="py-2">{msg}</Alert>}
      {error && <Alert variant="danger" className="py-2">{error}</Alert>}

      {loading || !form ? (
        <Spinner animation="border" size="sm" role="status" />
      ) : (
        <Form onSubmit={save}>
          <Row className="g-2">
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small mb-0">Registered company name</Form.Label>
                <Form.Control size="sm" value={form.registeredName} onChange={set('registeredName')} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small mb-0">Registration number</Form.Label>
                <Form.Control size="sm" value={form.registrationNumber} onChange={set('registrationNumber')} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small mb-0">Registered since</Form.Label>
                <Form.Control size="sm" type="date" value={form.registeredSince} onChange={set('registeredSince')} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small mb-0">Years of experience</Form.Label>
                <Form.Control size="sm" type="number" min="0" max="200" value={form.yearsExperience} onChange={set('yearsExperience')} />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group className="mb-3">
                <Form.Label className="small mb-0">About this business</Form.Label>
                <Form.Control size="sm" as="textarea" rows={3} value={form.about} onChange={set('about')} />
              </Form.Group>
            </Col>
          </Row>

          <h6 className="mt-2">Management team</h6>
          {form.management.map((m, idx) => (
            <Row key={idx} className="g-2 align-items-start border rounded p-2 mb-2 mx-0">
              <Col xs={12} md={3} className="text-center">
                {m.photoUrl ? (
                  <img
                    src={imageUrl(m.photoUrl)} alt={m.name || 'Management'}
                    style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: '50%' }}
                  />
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center bg-light text-muted mx-auto"
                    style={{ width: 64, height: 64, borderRadius: '50%' }}
                  >
                    Photo
                  </div>
                )}
                <Form.Control
                  size="sm" type="file" accept="image/*" className="mt-1"
                  disabled={uploadingIdx === idx}
                  onChange={(e) => uploadMemberPhoto(idx, e.target.files?.[0])}
                />
                {uploadingIdx === idx && <Spinner animation="border" size="sm" role="status" className="mt-1" />}
              </Col>
              <Col xs={12} md={9}>
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Control size="sm" placeholder="Name" value={m.name} onChange={setMember(idx, 'name')} />
                  </Col>
                  <Col md={6}>
                    <Form.Control size="sm" placeholder="Designation" value={m.designation} onChange={setMember(idx, 'designation')} />
                  </Col>
                  <Col xs={12}>
                    <Form.Control
                      size="sm" as="textarea" rows={2} placeholder="Short bio"
                      value={m.bio} onChange={setMember(idx, 'bio')}
                    />
                  </Col>
                </Row>
                <Button size="sm" variant="outline-danger" className="mt-1" onClick={() => removeMember(idx)}>
                  Remove
                </Button>
              </Col>
            </Row>
          ))}
          <Button size="sm" variant="outline-secondary" className="mb-3" onClick={addMember}>
            Add management profile
          </Button>

          <div>
            <Button size="sm" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save business profile'}</Button>
          </div>
        </Form>
      )}
    </div>
  );
}
